import { makeVar } from '@apollo/client';
import { ReactiveListener } from '@apollo/client/cache/inmemory/reactiveVars';

import { setupSingleton } from '@occc-app/Activate/api';
import config from '@occc/config';

import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY, tokenService, type AuthTokens } from './tokenService';

export const accessTokenVar = makeVar<string>('');
export const refreshTokenVar = makeVar<string>('');
export const isLoggedInVar = makeVar<boolean>(false);

const accessTokenOnNextChange: ReactiveListener<string> = (token) => {
  isLoggedInVar(!!token);
  setupSingleton(config.api.host, token);
  accessTokenVar.onNextChange(accessTokenOnNextChange);
};
accessTokenVar.onNextChange(accessTokenOnNextChange);

type ApplyTokensOptions = {
  /**
   * Whether to mirror the tokens to the shared store (localStorage). Local
   * changes (login, successful refresh) persist; changes synced *from* another
   * tab pass `false` so we don't echo the write back and start a sync loop.
   */
  persist: boolean;
};

// Keeps the in-memory reactive vars in sync with localStorage
export const applyTokens = (
  { accessToken, refreshToken }: AuthTokens,
  { persist }: ApplyTokensOptions,
): void => {
  if (persist) {
    if (accessToken) {
      tokenService.setAccessToken(accessToken);
    } else {
      tokenService.clearAccessToken();
    }

    if (refreshToken) {
      tokenService.setRefreshToken(refreshToken);
    } else {
      tokenService.clearRefreshToken();
    }
  }

  accessTokenVar(accessToken);
  refreshTokenVar(refreshToken);
};

export const syncReactiveAuthVariables = (): void => {
  const tokens = tokenService.getTokens();
  applyTokens(tokens, { persist: false });
};

let crossTabAuthSyncStarted = false;

// Syncs localStorage changes from other tabs to the reactive vars in this tab.
export const startCrossTabAuthSync = (): void => {
  if (crossTabAuthSyncStarted || typeof window === 'undefined') {
    return;
  }
  crossTabAuthSyncStarted = true;

  window.addEventListener('storage', (event) => {
    // Ignore changes to other storage areas (e.g. sessionStorage).
    if (event.storageArea && event.storageArea !== window.localStorage) {
      return;
    }

    // `key === null` means the whole store was cleared (e.g. localStorage.clear()).
    const tokenKeys: Array<string | null> = [null, ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY];

    if (tokenKeys.includes(event.key)) {
      syncReactiveAuthVariables();
    }
  });
};
